import axios from "axios";
import { API } from "./api";

let cachedTelegram = null;
let pageLoadAt = Date.now();
let maxScrollPct = 0;
let pageviewSent = false;

// ---------- session id ----------
function getSessionId() {
    if (typeof window === "undefined") return "ssr";
    let id = sessionStorage.getItem("mbe_session_id");
    if (!id) {
        id =
            (crypto.randomUUID && crypto.randomUUID()) ||
            `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        sessionStorage.setItem("mbe_session_id", id);
    }
    return id;
}

// ---------- device detection ----------
export function detectDevice() {
    if (typeof window === "undefined") return "desktop";
    const ua = navigator.userAgent || "";
    if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return "tablet";
    if (/Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua))
        return "mobile";
    // fallback: viewport width
    if (window.innerWidth <= 768) return "mobile";
    if (window.innerWidth <= 1024) return "tablet";
    return "desktop";
}

// ---------- geo lookup (cached per session) ----------
async function fetchGeo() {
    if (typeof window === "undefined") return {};
    const cached = sessionStorage.getItem("mbe_geo");
    if (cached) {
        try {
            return JSON.parse(cached);
        } catch {
            // ignore
        }
    }
    try {
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 2500);
        const res = await fetch("https://ipapi.co/json/", {
            signal: controller.signal,
        });
        clearTimeout(t);
        if (!res.ok) throw new Error("geo lookup failed");
        const data = await res.json();
        const geo = {
            country: data.country_name || null,
            country_code: data.country_code || null,
        };
        sessionStorage.setItem("mbe_geo", JSON.stringify(geo));
        return geo;
    } catch {
        const geo = { country: null, country_code: null };
        sessionStorage.setItem("mbe_geo", JSON.stringify(geo));
        return geo;
    }
}

// ---------- scroll depth tracking ----------
function computeScrollPct() {
    const doc = document.documentElement;
    const total = doc.scrollHeight - window.innerHeight;
    if (total <= 0) return 0;
    return Math.min(100, Math.round((window.scrollY / total) * 100));
}

export function initTracking() {
    if (typeof window === "undefined") return;
    pageLoadAt = Date.now();
    maxScrollPct = computeScrollPct();

    const onScroll = () => {
        const p = computeScrollPct();
        if (p > maxScrollPct) maxScrollPct = p;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Fire pageview once geo resolves
    (async () => {
        if (pageviewSent) return;
        pageviewSent = true;
        const geo = await fetchGeo();
        try {
            await axios.post(
                `${API}/track/pageview`,
                {
                    session_id: getSessionId(),
                    path: window.location.pathname,
                    country: geo.country,
                    country_code: geo.country_code,
                    device: detectDevice(),
                    referrer: document.referrer || null,
                },
                { timeout: 4000 }
            );
        } catch {
            // swallow
        }
    })();

    return () => window.removeEventListener("scroll", onScroll);
}

export async function fetchTelegramLink() {
    if (cachedTelegram) return cachedTelegram;
    try {
        const { data } = await axios.get(`${API}/config`);
        cachedTelegram = data.telegram_link || "https://t.me/mbealert";
    } catch {
        cachedTelegram = "https://t.me/mbealert";
    }
    return cachedTelegram;
}

/**
 * Fire-and-forget click tracker. Always opens the Telegram link in a new tab.
 */
export async function trackAndRedirect(location, extra = {}) {
    let geo = {};
    try {
        const cached = sessionStorage.getItem("mbe_geo");
        geo = cached ? JSON.parse(cached) : await fetchGeo();
    } catch {
        // ignore
    }

    const payload = {
        location,
        label: extra.label || null,
        section: extra.section || null,
        path: typeof window !== "undefined" ? window.location.pathname : null,
        country: geo.country || null,
        country_code: geo.country_code || null,
        device: detectDevice(),
        scroll_depth: maxScrollPct,
        time_on_page_ms: Date.now() - pageLoadAt,
        session_id: getSessionId(),
    };

    // Fire Meta Pixel Subscribe event for every CTA click
    try {
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
            window.fbq("track", "Subscribe", {
                content_name: extra.label || "MBE Alert Trial CTA",
                content_category: extra.section || "general",
                source: location,
                country: geo.country_code || undefined,
                device: payload.device,
            });
        }
    } catch {
        // ignore pixel errors so they never block redirect
    }

    // Open a placeholder window first to avoid popup blockers.
    const newWin =
        typeof window !== "undefined" ? window.open("about:blank", "_blank") : null;

    const openDest = (dest) => {
        if (!dest) return;
        if (newWin) {
            try {
                newWin.location.href = dest;
                return;
            } catch {
                // ignore and fallback
            }
        }
        // Popup blocked or failed — fallback to same-tab navigation so the
        // user still reaches Telegram.
        try {
            window.location.assign(dest);
        } catch {
            window.open(dest, "_blank");
        }
    };

    try {
        const { data } = await axios.post(`${API}/track/click`, payload, {
            timeout: 4000,
        });
        openDest(data?.redirect || (await fetchTelegramLink()));
    } catch {
        openDest(await fetchTelegramLink());
    }
}
